/**
 * MatricePrelieviPage — Vista matrice UL × Verbali di Prelievo + Referti.
 * Stile simile alla "Matrice Documenti" ma per i prelievi HACCP.
 * Visibile a: amministratore, consulente HACCP, azienda, laboratorio.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { FileText, Download, TestTube, FlaskConical, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const STATUS_LABEL = {
  bozza: { label: 'Bozza', color: 'bg-slate-100 text-slate-700' },
  pronto: { label: 'Pronto', color: 'bg-blue-100 text-blue-800' },
  inviato_lab: { label: 'Inviato Lab', color: 'bg-amber-100 text-amber-800' },
  in_analisi: { label: 'In Analisi', color: 'bg-violet-100 text-violet-800' },
  referto_ricevuto: { label: 'Referto Ricevuto', color: 'bg-green-100 text-green-800' },
};

export default function MatricePrelieviPage() {
  const [matrice, setMatrice] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/verbali-prelievo/matrice/by-ul')
      .then(r => setMatrice(r.data.matrice || []))
      .catch(e => toast.error('Errore: ' + (e?.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, []);

  const downloadReferto = async (id, numero) => {
    try {
      const resp = await api.get(`/verbali-prelievo/${id}/referto`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Referto_${(numero || '').replace('/', '-')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Referto non disponibile: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const downloadVerbale = async (id, numero) => {
    try {
      const resp = await api.post(`/verbali-prelievo/${id}/generate-docx`, null, { responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Verbale_${(numero || '').replace('/', '-')}.docx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Errore: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const downloadMod1 = async (id, numero) => {
    try {
      const resp = await api.get(`/verbali-prelievo/${id}/mod1-accettazione`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Mod1_Accettazione_${(numero || '').replace('/', '-')}.docx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Errore Mod.1: ' + (e?.response?.data?.detail || e.message));
    }
  };

  if (loading) return <div className="p-8 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento matrice…</div>;

  const tot = matrice.reduce((acc, row) => acc + row.tot_verbali, 0);
  const totRef = matrice.reduce((acc, row) => acc + row.tot_con_referto, 0);

  return (
    <div className="space-y-4 p-4 max-w-7xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold" data-testid="matrice-title">Storico Campionamenti</h1>
          <p className="text-sm text-slate-600">Storico completo dei prelievi per ciascuna Unità Locale — verbali emessi, modulo accettazione lab, rapporti di prova.</p>
        </div>
        <Link to="/haccp/verbali-prelievo">
          <Button data-testid="back-to-list-btn"><TestTube className="w-4 h-4 mr-1" /> Vai ai Verbali</Button>
        </Link>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="pt-4"><div className="text-3xl font-bold">{matrice.length}</div><div className="text-xs text-slate-600">Unità Locali con prelievi</div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-3xl font-bold">{tot}</div><div className="text-xs text-slate-600">Verbali totali</div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-3xl font-bold text-green-700">{totRef}</div><div className="text-xs text-slate-600">Referti ricevuti ({tot ? Math.round((totRef / tot) * 100) : 0}%)</div></CardContent></Card>
      </div>

      {matrice.length === 0 ? (
        <Card>
          <CardContent className="pt-8 pb-8 text-center text-slate-600">
            <TestTube className="w-12 h-12 mx-auto text-slate-300 mb-2" />
            <p>Nessun verbale di prelievo registrato.</p>
          </CardContent>
        </Card>
      ) : (
        matrice.map((ul) => (
          <Card key={ul.pdv_id || 'no-ul'} data-testid={`ul-card-${ul.pdv_id || 'none'}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
                <div>
                  <span>{ul.pdv_nome}</span>
                  {ul.azienda_nome && <span className="text-xs text-slate-500 ml-2">· {ul.azienda_nome}</span>}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <Badge variant="outline">{ul.tot_verbali} verbali</Badge>
                  <Badge className={ul.tot_con_referto === ul.tot_verbali ? 'bg-green-600' : 'bg-amber-500'}>
                    {ul.tot_con_referto}/{ul.tot_verbali} con referto
                  </Badge>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr className="text-left">
                    <th className="p-2 text-xs">N° Verbale</th>
                    <th className="p-2 text-xs">Data</th>
                    <th className="p-2 text-xs">Campioni</th>
                    <th className="p-2 text-xs">Laboratorio</th>
                    <th className="p-2 text-xs">Stato</th>
                    <th className="p-2 text-xs">Referto</th>
                    <th className="p-2 text-xs text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {ul.verbali.map(v => {
                    const st = STATUS_LABEL[v.status] || { label: v.status, color: 'bg-slate-100' };
                    return (
                      <tr key={v.id} className="border-b hover:bg-slate-50">
                        <td className="p-2 font-mono">{v.numero_verbale || '—'}</td>
                        <td className="p-2">{v.data_prelievo || '—'}</td>
                        <td className="p-2"><Badge variant="outline" className="text-xs">{v.campionamenti_count}</Badge></td>
                        <td className="p-2 text-xs text-slate-600">{v.laboratorio_nome || '—'}</td>
                        <td className="p-2"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span></td>
                        <td className="p-2">
                          {v.has_referto ? (
                            <span className="inline-flex items-center gap-1 text-green-700 text-xs">
                              <CheckCircle2 className="w-3 h-3" /> Ricevuto
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-amber-700 text-xs">
                              <AlertCircle className="w-3 h-3" /> In attesa
                            </span>
                          )}
                        </td>
                        <td className="p-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" onClick={() => downloadVerbale(v.id, v.numero_verbale)} title="Scarica Verbale Word" data-testid={`dl-verbale-${v.id}`}>
                              <FileText className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => downloadMod1(v.id, v.numero_verbale)} title="Genera Mod.1 Accettazione" data-testid={`dl-mod1-${v.id}`}>
                              <FlaskConical className="w-3 h-3" />
                            </Button>
                            {v.has_referto && (
                              <Button size="sm" variant="ghost" onClick={() => downloadReferto(v.id, v.numero_verbale)} className="text-green-700" title="Scarica Referto PDF" data-testid={`dl-referto-${v.id}`}>
                                <Download className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}
